import type { Action, Reducer } from "@reduxjs/toolkit";
import {
    DiagnosticsState,
    DiagnosticsActionTypes,
    REPORT_DIAGNOSTIC,
    FINALIZE_PARSE,
    CLEAR_DIAGNOSTICS,
    CLEAR_DIAGNOSTICS_FOR_FILE,
} from "./types";

const initialState: DiagnosticsState = {
    byTemplate: {}
};

export const diagnosticsReducer: Reducer<DiagnosticsState, Action, DiagnosticsState> = (
    state = initialState,
    action: DiagnosticsActionTypes
): DiagnosticsState => {
    switch (action.type) {
        case REPORT_DIAGNOSTIC: {
            const existing = state.byTemplate[action.bucketKey];

            // A different parseId means a fresh parse pass — start a new bucket...
            if (!existing || existing.parseId !== action.parseId) {
                return {
                    ...state,
                    byTemplate: {
                        ...state.byTemplate,
                        [action.bucketKey]: {
                            fileName: action.fileName,
                            source: action.source,
                            parseId: action.parseId,
                            diagnostics: [action.diagnostic]
                        }
                    }
                };
            }

            // Same parse pass — append, de-duping by diagnostic id...
            if (existing.diagnostics.some(d => d.id === action.diagnostic.id)) {
                return state;
            }

            return {
                ...state,
                byTemplate: {
                    ...state.byTemplate,
                    [action.bucketKey]: {
                        ...existing,
                        diagnostics: [...existing.diagnostics, action.diagnostic]
                    }
                }
            };
        }

        case FINALIZE_PARSE: {
            const existing = state.byTemplate[action.bucketKey];
            // Nothing recorded, or this parse pass already populated the bucket (it had diagnostics)...
            if (!existing || existing.parseId === action.parseId) {
                return state;
            }

            // The content changed and this parse produced no diagnostics → it was fixed; clear stale ones...
            return {
                ...state,
                byTemplate: {
                    ...state.byTemplate,
                    [action.bucketKey]: {
                        ...existing,
                        parseId: action.parseId,
                        diagnostics: []
                    }
                }
            };
        }

        case CLEAR_DIAGNOSTICS_FOR_FILE: {
            // Short-circuit if no file name is provided...
            if (!state.byTemplate[action.fileName]) return state;

            const { [action.fileName]: diagnosticsForFile, ...rest } = state.byTemplate;

            // Only clear out a specific diagnostic for the file...
            if (action.diagnosticId) {
                const updated = { ...diagnosticsForFile, diagnostics: diagnosticsForFile.diagnostics.filter(d => d.id !== action.diagnosticId) };
                return { ...state, byTemplate: { ...rest, [action.fileName]: updated } };
            } 

            // Clear out all diagnostics for the file...
            return { ...state, byTemplate: rest };
        }

        case CLEAR_DIAGNOSTICS:
            return { ...state, byTemplate: {} };

        default:
            return state;
    }
};
